import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  TextField,
  Grid,
  Paper,
  Divider,
  Box,
  Chip,
  withStyles,
} from '@material-ui/core';
import { Add, Delete, FileCopy } from '@material-ui/icons';
import ScheduledOrdersToggle from './ScheduledOrdersToggle';
import { useHeadOfficeGate, useLocationGate } from '../team/Gated';
import { toast } from "react-toastify";

// Custom Green Switch for Open state
const GreenSwitch = withStyles({
  switchBase: {
    color: '#f44336',
    '&$checked': {
      color: '#4caf50',
    },
    '&$checked + $track': {
      backgroundColor: '#4caf50',
    },
  },
  checked: {},
  track: {
    backgroundColor: '#ffcdd2',
  },
})(Switch);

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 10,
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderBottom: '2px solid #e0e0e0',
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    fontSize: '0.8rem',
    color: '#666',
    marginBottom: theme.spacing(2),
  },
  policyNote: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: '#eceff1',
    borderLeft: '4px solid #607d8b',
    borderRadius: 6,
  },
  policyNoteText: {
    fontSize: '0.8rem',
    color: '#37474f',
  },
  daysContainer: {
    maxHeight: '500px',
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
  },
  daySection: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
  },
  dayHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  dayName: {
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  timeSlot: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: 'white',
    borderRadius: 6,
    // Let the Opens / Closes blocks wrap onto a second line on narrow phones
    // instead of overflowing the card horizontally.
    flexWrap: 'wrap',
  },
  timeField: {
    flex: 1,
    '& .MuiInputBase-root': {
      fontSize: '0.85rem',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.75rem',
      transform: 'translate(14px, -6px) scale(0.75)',
      backgroundColor: '#fff',
      padding: '0 4px',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },
  addSlotButton: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1),
  },
  actionButtons: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #e0e0e0',
  },
  statusChip: {
    marginLeft: theme.spacing(1),
    height: 22,
    fontSize: '0.7rem',
  },
  quickActions: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
  },
  presetsTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  presetButton: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.5),
    textTransform: 'none',
    transition: 'all 0.2s ease',
  },
  activePreset: {
    backgroundColor: '#667eea',
    color: '#fff',
    borderColor: '#667eea',
    '&:hover': {
      backgroundColor: '#5568d3',
      borderColor: '#5568d3',
    },
  },
  toText: {
    fontSize: '0.8rem',
    color: '#666',
  },
  copyButton: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1),
    marginLeft: theme.spacing(1),
  },
}));

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const DEFAULT_SLOT = { openTime: '09:00', closeTime: '21:00' };

// Every zone Lusuku trades in, plus UTC as an escape hatch. The PATCH endpoint
// validates whatever arrives against Intl, so a zone missing from this list is
// a one-line addition rather than a data problem.
const TIMEZONE_OPTIONS = [
  { value: 'Africa/Kampala', label: 'Africa/Kampala — Uganda (UTC+3)' },
  { value: 'Africa/Lusaka', label: 'Africa/Lusaka — Zambia (UTC+2)' },
  { value: 'Africa/Nairobi', label: 'Africa/Nairobi — Kenya (UTC+3)' },
  { value: 'Africa/Kigali', label: 'Africa/Kigali — Rwanda (UTC+2)' },
  { value: 'Africa/Dar_es_Salaam', label: 'Africa/Dar_es_Salaam — Tanzania (UTC+3)' },
  { value: 'UTC', label: 'UTC' },
];

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const toMinutes = (value) => {
  if (!TIME_RE.test(value || '')) return NaN;
  const [hour, minute] = value.split(':').map(Number);
  return hour * 60 + minute;
};
const isFullDay = (slot) => {
  const open = toMinutes(slot?.openTime);
  const close = toMinutes(slot?.closeTime);
  return open === close || (open === 0 && close === 1439);
};

const validateSchedule = (openHours) => {
  const errors = [];
  const intervals = [];
  DAYS_OF_WEEK.forEach((day, dayIndex) => {
    const dayData = openHours?.[day.key];
    if (!dayData) {
      errors.push(`${day.label} is missing`);
      return;
    }
    if (!dayData.isOpen) return;
    if (!Array.isArray(dayData.slots) || dayData.slots.length === 0) {
      errors.push(`${day.label} is marked as open but has no time slots`);
      return;
    }
    if (dayData.slots.some(isFullDay) && dayData.slots.length > 1) {
      errors.push(`${day.label} cannot combine a full-day slot with other slots`);
    }
    dayData.slots.forEach((slot, slotIndex) => {
      const open = toMinutes(slot?.openTime);
      const close = toMinutes(slot?.closeTime);
      if (!Number.isFinite(open) || !Number.isFinite(close)) {
        errors.push(`${day.label} slot ${slotIndex + 1} has an invalid time`);
        return;
      }
      const start = dayIndex * 1440 + open;
      const end = isFullDay(slot)
        ? start + 1440
        : dayIndex * 1440 + close + (close < open ? 1440 : 0);
      intervals.push({
        start,
        end,
        label: `${day.label} slot ${slotIndex + 1}`,
      });
    });
  });

  const week = 7 * 1440;
  const repeated = [
    ...intervals,
    ...intervals.map((entry) => ({
      ...entry,
      start: entry.start + week,
      end: entry.end + week,
    })),
  ].sort((a, b) => a.start - b.start || a.end - b.end);
  for (let index = 1; index < repeated.length; index += 1) {
    const previous = repeated[index - 1];
    const current = repeated[index];
    if (current.start < previous.end && previous.start < week) {
      errors.push(`${current.label} overlaps ${previous.label}`);
    }
  }
  return [...new Set(errors)];
};

const OpenHoursManager = ({
  openHours: openHoursProp,
  hours,
  initialHours,
  onSave,
  onValidationError,
  isLoading,
  isSaving,
  timezone = 'Africa/Kampala',
  // The ONLY vendor-specific thing in this component was a hardcoded
  // "your restaurant's" in the subtitle. Now that shops, pharmacies and brands
  // mount it too, the noun is a prop.
  vendorLabel = 'business',
  // The outlet these hours belong to, when they are an outlet's (an id or
  // "main"); absent, they are the vendor's own (head office only).
  location = null,
  // Hide the zone picker where the caller genuinely cannot persist it (an
  // outlet editor that saves against its parent, say).
  allowTimezoneEdit = true,
  // Mirrors the server's `tradingPolicy` (services/vendorAvailabilityService.js).
  // 'open_until_set': the vendor trades 24/7 until hours are saved, and the
  // saved hours are then enforced. Retail, wholesale, brands, healthcare.
  // 'quiet_until_published': hours are simply not advertised until saved.
  // Restaurants, and the default so older call sites keep their copy.
  tradingPolicy = 'quiet_until_published',
  // Scheduled orders v2. With both set, the "Accept scheduled orders while
  // closed" switch renders beside the hours and saves itself. Leave them out
  // on outlet editors: an outlet follows its parent's setting.
  vendorType,
  vendorId,
}) => {
  // `hours`/`isSaving` are retained for older publication editors; restaurant
  // screens use `openHours`/`isLoading`. `initialHours` was being passed by the
  // healthcare screens against a prop that never existed, so those modals
  // silently opened on the 09:00-21:00 blank default instead of the vendor's
  // real schedule — and saving it would have overwritten their actual hours.
  const initialOpenHours = openHoursProp || hours || initialHours;
  const busy = Boolean(isLoading || isSaving);
  // Vendor team permissions: hours are outlets:manage. The editor stays
  // readable; saving is what the role decides. An outlet's hours (`location`
  // given) are that branch's to run; the vendor's own hours are head
  // office's (4 Oct 2026).
  const headOfficeGate = useHeadOfficeGate('outlets:manage');
  const atLocation = useLocationGate('outlets:manage');
  const hoursGate = location ? atLocation(location) : headOfficeGate;
  const locked = busy || !hoursGate.allowed;
  const openUntilSet = tradingPolicy === 'open_until_set';
  const classes = useStyles();
  const [openHours, setOpenHours] = useState(() => {
    if (initialOpenHours) return initialOpenHours;
    
    // Default: All days open 9 AM - 9 PM
    const defaultHours = {};
    DAYS_OF_WEEK.forEach((day) => {
      defaultHours[day.key] = {
        isOpen: true,
        slots: [{ ...DEFAULT_SLOT }],
      };
    });
    return defaultHours;
  });
  
  const [activePreset, setActivePreset] = useState(null);
  // Every slot below is interpreted in THIS zone. It was display-only before,
  // and no screen in the product wrote it, so a vendor stamped with the wrong
  // zone at signup could not be corrected without a direct database write.
  const [zone, setZone] = useState(timezone || 'Africa/Kampala');

  useEffect(() => {
    if (initialOpenHours) {
      setOpenHours(initialOpenHours);
    }
  }, [initialOpenHours]);

  useEffect(() => {
    if (timezone) setZone(timezone);
  }, [timezone]);

  const handleDayToggle = (day) => {
    setOpenHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        isOpen: !prev[day].isOpen,
      },
    }));
  };

  const handleTimeChange = (day, slotIndex, field, value) => {
    setOpenHours((prev) => {
      const newSlots = prev[day].slots.map((slot, index) =>
        index === slotIndex ? { ...slot, [field]: value } : slot
      );
      return {
        ...prev,
        [day]: {
          ...prev[day],
          slots: newSlots,
        },
      };
    });
  };

  const addTimeSlot = (day) => {
    setOpenHours((prev) => {
      const slots = prev[day].slots || [];
      if (slots.length >= 5) {
        toast.warn('Maximum 5 time slots per day');
        return prev;
      }
      return {
        ...prev,
        [day]: {
          ...prev[day],
          slots: [...slots, { ...DEFAULT_SLOT }],
        },
      };
    });
  };

  const removeTimeSlot = (day, slotIndex) => {
    setOpenHours((prev) => {
      const newSlots = prev[day].slots.filter((_, index) => index !== slotIndex);
      return {
        ...prev,
        [day]: {
          ...prev[day],
          slots: newSlots.length > 0 ? newSlots : [{ ...DEFAULT_SLOT }],
        },
      };
    });
  };

  const copyToAllDays = (sourceDay) => {
    const sourceDayData = openHours[sourceDay];
    const newHours = {};
    DAYS_OF_WEEK.forEach((day) => {
      newHours[day.key] = {
        isOpen: sourceDayData.isOpen,
        slots: sourceDayData.slots.map((slot) => ({ ...slot })),
      };
    });
    setOpenHours(newHours);
  };

  const applyPreset = (preset) => {
    const presets = {
      weekdays: () => {
        const newHours = {};
        DAYS_OF_WEEK.forEach((day, index) => {
          newHours[day.key] = {
            isOpen: index < 5, // Monday to Friday
            slots: index < 5 ? [{ openTime: '09:00', closeTime: '17:00' }] : [],
          };
        });
        return newHours;
      },
      allDay: () => {
        const newHours = {};
        DAYS_OF_WEEK.forEach((day) => {
          newHours[day.key] = {
            isOpen: true,
            // Equal midnight endpoints are the explicit full-day convention.
            // The API also understands legacy 00:00→23:59 schedules.
            slots: [{ openTime: '00:00', closeTime: '00:00' }],
          };
        });
        return newHours;
      },
      restaurant: () => {
        const newHours = {};
        DAYS_OF_WEEK.forEach((day) => {
          newHours[day.key] = {
            isOpen: true,
            slots: [
              { openTime: '11:00', closeTime: '14:00' },
              { openTime: '18:00', closeTime: '22:00' },
            ],
          };
        });
        return newHours;
      },
    };

    if (presets[preset]) {
      setOpenHours(presets[preset]());
      setActivePreset(preset);
    }
  };

  const handleSave = () => {
    const errors = validateSchedule(openHours);

    if (errors.length > 0) {
      if (onValidationError) {
        onValidationError(errors.join(', '));
      }
      return;
    }

    // Second argument, so a caller that has not been updated keeps working
    // exactly as before and simply does not persist the zone.
    onSave(openHours, zone);
  };

  return (
    <div className={classes.root}>
      {/* Sticky Header Section */}
      <div className={classes.stickyHeader}>
        <Typography className={classes.title}>
          Business Hours
        </Typography>
        <Typography className={classes.subtitle}>
          Set your {vendorLabel}&rsquo;s operating hours.
          {' '}
          {openUntilSet
            ? `Until you save hours, this ${vendorLabel} accepts orders around the clock. Save hours only if you want to limit ordering to specific times; once saved, customers can only order during them.`
            : 'Customers will only be able to order during these times.'}
          {' '}A closing time earlier than the opening time means the slot
          continues past midnight.
        </Typography>

        {openUntilSet && !initialOpenHours && (
          // The 09:00 to 21:00 grid below is the editor's blank starting point,
          // not the vendor's schedule. Without this note it reads as if those
          // hours were already in force.
          <Box className={classes.policyNote}>
            <Typography className={classes.policyNoteText}>
              No hours saved yet: customers can order from this {vendorLabel} 24/7 right now.
              {' '}The schedule below is only a starting point and is not applied until you press Save.
            </Typography>
          </Box>
        )}

        {allowTimezoneEdit ? (
          <Box mb={2} style={{ maxWidth: 340 }}>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Timezone"
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Every time below is read in this zone."
            >
              {(TIMEZONE_OPTIONS.some((option) => option.value === zone)
                ? TIMEZONE_OPTIONS
                : [{ value: zone, label: `${zone} (current)` }, ...TIMEZONE_OPTIONS]
              ).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
          </Box>
        ) : (
          <Typography className={classes.subtitle}>
            Times use <strong>{zone}</strong>.
          </Typography>
        )}

        <ScheduledOrdersToggle vendorType={vendorType} vendorId={vendorId} disabled={locked} />

        <Paper className={classes.quickActions}>
          <Typography className={classes.presetsTitle}>
            Quick Presets
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              className={`${classes.presetButton} ${activePreset === 'weekdays' ? classes.activePreset : ''}`}
              variant={activePreset === 'weekdays' ? 'contained' : 'outlined'}
              onClick={() => applyPreset('weekdays')}
            >
              Weekdays (9-5)
            </Button>
            <Button
              className={`${classes.presetButton} ${activePreset === 'allDay' ? classes.activePreset : ''}`}
              variant={activePreset === 'allDay' ? 'contained' : 'outlined'}
              onClick={() => applyPreset('allDay')}
            >
              24/7
            </Button>
            <Button
              className={`${classes.presetButton} ${activePreset === 'restaurant' ? classes.activePreset : ''}`}
              variant={activePreset === 'restaurant' ? 'contained' : 'outlined'}
              onClick={() => applyPreset('restaurant')}
            >
              Lunch & Dinner
            </Button>
          </Box>
        </Paper>
      </div>

      {/* Scrollable Days Container */}
      <div className={classes.daysContainer}>
        {DAYS_OF_WEEK.map((day) => {
        const dayData = openHours[day.key];
        return (
          <Paper key={day.key} className={classes.daySection}>
            <div className={classes.dayHeader}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography className={classes.dayName}>
                  {day.label}
                </Typography>
                {dayData.isOpen ? (
                  <Chip
                    label="Open"
                    size="small"
                    className={classes.statusChip}
                    style={{ backgroundColor: '#4caf50', color: 'white' }}
                  />
                ) : (
                  <Chip
                    label="Closed"
                    size="small"
                    className={classes.statusChip}
                    style={{ backgroundColor: '#f44336', color: 'white' }}
                  />
                )}
              </div>
              <FormControlLabel
                control={
                  <GreenSwitch
                    checked={dayData.isOpen}
                    onChange={() => handleDayToggle(day.key)}
                    size="small"
                  />
                }
                label={<span style={{ fontSize: '0.85rem' }}>{dayData.isOpen ? 'Open' : 'Closed'}</span>}
              />
            </div>

            {dayData.isOpen && (
              <>
                {dayData.slots.map((slot, index) => (
                  <div key={index} className={classes.timeSlot}>
                    <div style={{ flex: '1 1 120px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Typography variant="caption" style={{ fontSize: '0.7rem', color: '#666', marginLeft: '4px' }}>
                        Opens
                      </Typography>
                      <TextField
                        type="time"
                        value={slot.openTime}
                        onChange={(e) =>
                          handleTimeChange(day.key, index, 'openTime', e.target.value)
                        }
                        className={classes.timeField}
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </div>
                    <Typography className={classes.toText} style={{ alignSelf: 'center', marginTop: '16px' }}>to</Typography>
                    <div style={{ flex: '1 1 120px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Typography variant="caption" style={{ fontSize: '0.7rem', color: '#666', marginLeft: '4px' }}>
                        Closes
                      </Typography>
                      <TextField
                        type="time"
                        value={slot.closeTime}
                        onChange={(e) =>
                          handleTimeChange(day.key, index, 'closeTime', e.target.value)
                        }
                        className={classes.timeField}
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </div>
                    {dayData.slots.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeTimeSlot(day.key, index)}
                        color="secondary"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                ))}

                <Box display="flex" gap={1}>
                  {dayData.slots.length < 5 && (
                    <Button
                      startIcon={<Add fontSize="small" />}
                      onClick={() => addTimeSlot(day.key)}
                      className={classes.addSlotButton}
                    >
                      Add Slot
                    </Button>
                  )}
                  <Button
                    startIcon={<FileCopy fontSize="small" />}
                    onClick={() => copyToAllDays(day.key)}
                    className={classes.copyButton}
                  >
                    Copy to All
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        );
      })}
      </div>

      <div className={classes.actionButtons}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={locked}
          title={hoursGate.title}
        >
          {busy ? 'Saving...' : 'Save Business Hours'}
        </Button>
      </div>
    </div>
  );
};

export default OpenHoursManager;
